import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { debugLog, debugWarn, debugError } from '../../utils/debug.js';
/**
 * Google Analytics 컴포넌트
 * KV에서 GA 측정 ID를 가져와서 동적으로 스크립트 등록
 * 페이지 뷰 및 커스텀 이벤트 추적
 */
function GoogleAnalytics() {
  const [gaCode, setGaCode] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const location = useLocation();

  // KV에서 GA 코드 가져오기
  useEffect(() => {
    const fetchGACode = async () => {
      try {
        const response = await fetch('/api/analytics/code');
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.code) {
            debugLog('[GA] Loaded GA code from KV:', data.code);
            setGaCode(data.code);
          }
        }
      } catch (error) {
        debugError('[GA] Failed to fetch GA code:', error);
      }
    };

    fetchGACode();
  }, []);

  // GA 스크립트 초기화
  useEffect(() => {
    if (!gaCode || isInitialized) return;

    debugLog('[GA] Initializing Google Analytics with code:', gaCode);

    // gtag.js 스크립트 로드
    const script1 = document.createElement('script');
    script1.async = true;
    script1.src = `https://www.googletagmanager.com/gtag/js?id=${gaCode}`;
    document.head.appendChild(script1);

    // gtag 초기화 스크립트
    const script2 = document.createElement('script');
    script2.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${gaCode}', {
        send_page_view: false
      });
    `;
    document.head.appendChild(script2);

    // window.gtag 함수 생성
    window.dataLayer = window.dataLayer || [];
    window.gtag = function() {
      window.dataLayer.push(arguments);
    };

    setIsInitialized(true);
    debugLog('[GA] Google Analytics initialized');

    return () => {
      // 클린업 (필요시)
    };
  }, [gaCode, isInitialized]);

  // 페이지 뷰 추적
  useEffect(() => {
    if (!isInitialized || !window.gtag) return;

    // QR 정보 가져오기
    let qrCode = null;
    let location_name = null;
    try {
      const qrInfoStr = sessionStorage.getItem('qr_info');
      if (qrInfoStr) {
        const qrInfo = JSON.parse(qrInfoStr);
        qrCode = qrInfo.qrCode || qrInfo.qr_code_id || qrInfo.serialNumber || qrInfo.serial_number || 'unknown';
        location_name = qrInfo.location || 'unknown';
      }
    } catch (err) {
      debugError('[GA] Failed to get QR info:', err);
    }

    // 페이지 이름 매핑
    const pageMap = {
      '/': 'Home',
      '/caricature': 'ImageSelection',
      '/caricature/generation': 'StyleSelection',
      '/caricature/progress': 'AIGeneration',
      '/caricature/result': 'PrintResult'
    };

    const pageName = pageMap[location.pathname] || location.pathname;

    debugLog('[GA] Page view:', {
      page_path: location.pathname,
      page_title: pageName,
      qr_code: qrCode,
      location: location_name
    });

    // 페이지 뷰 전송
    window.gtag('event', 'page_view', {
      page_path: location.pathname,
      page_title: pageName,
      qr_code: qrCode,
      location: location_name
    });
  }, [location, isInitialized]);

  return null; // 렌더링 없음
}

/**
 * GA 이벤트 전송 헬퍼 함수
 * @param {string} eventName - 이벤트 이름
 * @param {object} params - 이벤트 파라미터
 */
export function sendGAEvent(eventName, params = {}) {
  if (!window.gtag) {
    debugWarn('[GA] gtag not initialized yet');
    return;
  }

  // QR 정보 추가
  let qrCode = null;
  let location_name = null;
  try {
    const qrInfoStr = sessionStorage.getItem('qr_info');
    if (qrInfoStr) {
      const qrInfo = JSON.parse(qrInfoStr);
      qrCode = qrInfo.qrCode || qrInfo.qr_code_id || qrInfo.serialNumber || qrInfo.serial_number || 'unknown';
      location_name = qrInfo.location || 'unknown';
    }
  } catch (err) {
    // 에러 무시
  }

  const eventParams = {
    ...params,
    qr_code: qrCode,
    location: location_name
  };

  debugLog('[GA] Event:', eventName, eventParams);
  window.gtag('event', eventName, eventParams);
}

/**
 * 퍼널 이벤트 전송 (GA + 서버 사이드 동시)
 * GA 차단(애드블록) 대비 서버 로그도 병행
 */
export function sendFunnelEvent(eventName, params = {}) {
  // 1. GA 이벤트 전송
  sendGAEvent(eventName, params);

  // 2. 서버 사이드 로그 (비동기, 실패해도 무시)
  let session_id = null;
  let customer_id = null;
  let location_name = null;

  try {
    const qrInfoStr = sessionStorage.getItem('qr_info');
    if (qrInfoStr) {
      const qrInfo = JSON.parse(qrInfoStr);
      location_name = qrInfo.location || null;
    }
  } catch (e) { /* ignore */ }

  try {
    const sid = new URLSearchParams(window.location.search).get('sid');
    if (sid) session_id = sid;
  } catch (e) { /* ignore */ }

  fetch('/api/analytics/funnel-event', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      event_name: eventName,
      event_params: params,
      session_id,
      customer_id,
      location: location_name
    })
  }).catch(() => {
    // 서버 로그 실패는 무시 (GA가 메인)
  });
}

export default GoogleAnalytics;
